import {
	dataLayerPush,
	type CtaClickEvent,
	type FileDownloadEvent,
	type ProductImageUpdateEvent,
	type SelectItemOptionEvent,
} from '@ds-mgnl/marketing';
import type { ProductAssetItem } from '../../services/productApi';

interface ProductIdentity {
	id?: string;
	name?: string;
}

export function pushSelectItemOption(
	product: ProductIdentity,
	options: { optionType: string; optionValue: string; viewerActive: boolean },
): void {
	const selectItemEvent: SelectItemOptionEvent = {
		event: 'select_item_option',
		option_type: options.optionType,
		option_value: options.optionValue,
		viewer_active: options.viewerActive,
		product_id: product.id ?? '',
		product_name: product.name ?? '',
	};
	dataLayerPush(selectItemEvent);
}

export function pushProductImageUpdate(
	product: ProductIdentity,
	options: { optionType: string; optionValue: string; imageContext: string },
): void {
	const productImageUpdate: ProductImageUpdateEvent = {
		event: 'product_image_update',
		option_type: options.optionType,
		option_value: options.optionValue,
		image_context: options.imageContext,
		product_id: product.id ?? '',
		product_name: product.name ?? '',
	};
	dataLayerPush(productImageUpdate);
}

export function pushFileDownloadEvent(
	product: ProductIdentity,
	asset: ProductAssetItem,
	fileType?: string,
): void {
	const fileDownloadEvent: FileDownloadEvent = {
		event: 'file_download',
		file_name: asset.altText ?? '',
		product_id: product.id ?? '',
		product_name: product.name ?? '',
		file_type: fileType || (asset.type ?? ''),
		link_url: asset.contentUrl ?? '',
	};
	dataLayerPush(fileDownloadEvent);
}

interface CtaNavigationOptions {
	/** Destination path, resolved against window.location.href. */
	path: string;
	/** Query params to attach; null/undefined/empty-string values are omitted. */
	params: Record<string, unknown>;
	ctaLabel: string;
	ctaLocation: string;
	product: ProductIdentity;
	extraEventFields?: Record<`selected_${string}`, string | undefined>;
}

/** Builds the CTA destination URL, pushes a cta_click event, and returns the navigate() target. */
export function buildCtaNavigation({
	path,
	params,
	ctaLabel,
	ctaLocation,
	product,
	extraEventFields,
}: CtaNavigationOptions): { pathname: string; search: string } {
	const url = new URL(path, window.location.href);

	for (const [key, value] of Object.entries(params)) {
		if (value != null && value !== '') {
			url.searchParams.set(key, String(value));
		}
	}

	const ctaClickEvent: CtaClickEvent = {
		event: 'cta_click',
		cta_label: ctaLabel,
		cta_location: ctaLocation,
		product_id: product.id ?? '',
		product_name: product.name ?? '',
		cta_destination: url.toString(),
		...extraEventFields,
	};
	dataLayerPush(ctaClickEvent);

	return { pathname: url.pathname, search: url.search };
}
